import type { FilterCategory } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/category";
import type {
    CategoryFiltersConfig,
    FiltersConfig,
    FiltersConfigStatus,
} from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filtersConfig";
import type { OrderedSearchFilterInstance } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/searchFilterInstance";

const computeMergedStatus = (results: readonly FiltersConfig[]): FiltersConfigStatus => {
    if (results.some((r) => r.status === "pending")) {
        return "pending";
    }

    if (results.every((r) => r.status === "ready")) {
        return "ready";
    }

    return "initial";
};

const mergeFiltersConfigs = <F extends OrderedSearchFilterInstance>(
    ...results: readonly FiltersConfig<readonly F[]>[]
): FiltersConfig<F[]> => {
    const filtersList = results
        .flatMap((r) => r.filtersList)
        .filter(Boolean)
        .sort((a, b) => a.order - b.order);

    if (process.env.NODE_ENV === "development") {
        const keys = filtersList.map((f) => f.key);
        const duplicates = keys.filter((key, index) => keys.indexOf(key) !== index);
        if (duplicates.length > 0) {
            throw new Error(
                `[mergeFiltersConfigs] Duplicate filter keys detected: ${[...new Set(duplicates)].join(", ")}`,
            );
        }
    }

    return {
        filtersList,
        status: computeMergedStatus(results),
    };
};

export const mergeCategoryFiltersConfigs = <F extends OrderedSearchFilterInstance>(
    ...configs: readonly CategoryFiltersConfig<F>[]
): CategoryFiltersConfig<F> => {
    const { filtersList, status } = mergeFiltersConfigs<F>(...configs);

    const seen = new Set<FilterCategory>();
    const categoriesList = configs
        .flatMap((c) => c.categoriesList)
        .filter((category) => {
            if (seen.has(category.id)) {
                return false;
            }

            seen.add(category.id);

            return true;
        })
        .sort((a, b) => a.order - b.order);

    return { filtersList, status, categoriesList };
};
